import { useTranslation } from '@rhino-project/core/i18n';
import type { Key } from 'react';
import { useEffect, useState } from 'react';

import { BaseAuthedPage } from '@rhino-project/ui-heroui';
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectIndicator,
  SelectPopover,
  ListBox,
  ListBoxItem
} from '@heroui/react';
import { useModelIndex } from '@rhino-project/core/hooks';
import { usePlanningSeasons } from '../../hooks/ots';
import Lineup from '../../components/lineup/Lineup';
import type { RhinoRead } from '@rhino-project/core';

// Rhino adds display_name at runtime; cast to access computed fields
const GhostPage = () => {
  const { t } = useTranslation();

  const [teamSeason, setTeamSeason] = useState<string | number>('');
  const { resources: planningSeasons } = usePlanningSeasons();

  const { isPending, resources: lineups } = useModelIndex('lineup', {
    filter: { team_season_id: teamSeason, kind: 'depth_chart' },
    queryOptions: { enabled: teamSeason !== '' }
  });

  const handleChange = (key: Key | null) => {
    if (key !== null) setTeamSeason(key as string | number);
  };

  // Set a default if there was none and there is one now
  useEffect(() => {
    if (teamSeason !== '' || !planningSeasons?.results?.[0]) return;

    // eslint-disable-next-line @eslint-react/set-state-in-effect, react-hooks/set-state-in-effect -- Preserve existing behavior while enabling the rule globally.
    setTeamSeason(planningSeasons.results[0].id!);
  }, [planningSeasons, teamSeason, setTeamSeason]);

  return (
    <BaseAuthedPage>
      <Select
        value={teamSeason}
        onChange={handleChange}
        aria-label={t('frontend.messages.select_season')}
      >
        <SelectTrigger>
          <SelectValue />
          <SelectIndicator />
        </SelectTrigger>
        <SelectPopover>
          <ListBox items={planningSeasons?.results || []}>
            {(planningSeason: RhinoRead<'team_season'>) => (
              <ListBoxItem
                id={planningSeason.id}
                key={planningSeason.id}
                textValue={planningSeason.display_name as string | undefined}
              >
                {planningSeason.display_name}
              </ListBoxItem>
            )}
          </ListBox>
        </SelectPopover>
      </Select>
      {isPending
        ? null
        : (lineups?.total ?? 0) > 0 &&
          lineups && <Lineup lineup={lineups.results[0]} />}
    </BaseAuthedPage>
  );
};

export default GhostPage;
